/**
 * Colours and gradients for the 2D stand-in, as the GPU wants them: RGBA in
 * 0..1, premultiplied by alpha.
 */

/** A premultiplied RGBA colour. */
export type Rgba = [r: number, g: number, b: number, a: number]

const MAX_CACHED = 4000
let cache = new Map<string, Rgba>()

/** A CSS colour as `#rgb`, `#rrggbb`, `rgb(...)` or `rgba(...)`, premultiplied. */
export function parseColor(css: string): Rgba {
  const cached = cache.get(css)
  if (cached) return cached
  let r: number, g: number, b: number, a: number
  if (css.charAt(0) === '#') {
    const hex = css.length === 4 ? css.replace(/#(.)(.)(.)/, '#$1$1$2$2$3$3') : css
    r = parseInt(hex.slice(1, 3), 16)
    g = parseInt(hex.slice(3, 5), 16)
    b = parseInt(hex.slice(5, 7), 16)
    a = 1
  } else {
    const parts = css.substring(css.indexOf('(') + 1, css.lastIndexOf(')')).split(',')
    r = +parts[0]
    g = +parts[1]
    b = +parts[2]
    a = parts.length > 3 ? +parts[3] : 1
  }
  const color: Rgba = [(r / 255) * a, (g / 255) * a, (b / 255) * a, a]
  if (cache.size > MAX_CACHED) cache = new Map()
  cache.set(css, color)
  return color
}

/** A linear gradient from (x0, y0) to (x1, y1), or a radial one between two circles. */
export class Gradient implements CanvasGradient {
  stops: [offset: number, color: Rgba][] = []

  constructor(
    readonly type: 'linear' | 'radial',
    /** `[x0, y0, x1, y1]`, or `[x0, y0, r0, x1, y1, r1]`. */
    readonly geometry: number[]
  ) {}

  addColorStop(offset: number, color: string) {
    this.stops.push([offset, parseColor(color)])
    this.stops.sort((x, y) => x[0] - y[0])
  }

  /** The colour at `t` (0 at the start, 1 at the end), times `alpha`, into `out`. */
  at(t: number, out: Rgba, alpha: number): Rgba {
    const stops = this.stops
    const n = stops.length
    if (!n) {
      out[0] = out[1] = out[2] = out[3] = 0
      return out
    }
    let c: Rgba
    if (t <= stops[0][0]) c = stops[0][1]
    else if (t >= stops[n - 1][0]) c = stops[n - 1][1]
    else {
      let i = 1
      while (i < n && stops[i][0] < t) i++
      const [ta, a] = stops[i - 1]
      const [tb, b] = stops[i]
      const u = (t - ta) / Math.max(tb - ta, 1e-6)
      for (let k = 0; k < 4; k++) out[k] = (a[k] + (b[k] - a[k]) * u) * alpha
      return out
    }
    for (let k = 0; k < 4; k++) out[k] = c[k] * alpha
    return out
  }
}
